import React, { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { campaignService } from '../services/campaignService';
import { Campaign } from '../types';
import { CampaignImage } from '../components/CampaignImage';
import { Icon } from '../components/participant/Icon';
import { ParticipantTopBar } from '../components/participant/ParticipantTopBar';
import { participantColors as c, fadeUpKeyframes } from '../theme/participantTheme';

const formatDate = (dateString: string): string => new Date(dateString).toLocaleDateString('pt-BR');

const UserCampaignList: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await campaignService.getActiveCampaigns();
        setCampaigns(data);
      } catch (err: any) {
        if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
          setError('Backend não está disponível. Verifique se o servidor está rodando.');
        } else if (err.response?.status === 401) {
          setError('Sessão expirada. Faça login novamente.');
        } else if (err.response?.status === 403) {
          setError('Acesso negado. Verifique suas permissões.');
        } else {
          setError(err.response?.data?.message || 'Erro ao carregar campanhas. Tente novamente.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', background: c.gradients.pageBg, fontFamily: c.fontFamily, pb: '48px' }}>
      <style>{fadeUpKeyframes}</style>

      <Box sx={{ maxWidth: 620, mx: 'auto', px: 2, display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <ParticipantTopBar />

        {/* Cabeçalho da página */}
        <Box
          sx={{
            background: c.surface,
            borderRadius: `${c.radius.card}px`,
            padding: '20px',
            boxShadow: c.shadows.card,
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <Box
            component="button"
            onClick={() => navigate('/dashboard')}
            title="Voltar"
            sx={{
              width: 52,
              height: 52,
              flexShrink: 0,
              border: `2px solid ${c.border}`,
              borderRadius: `${c.radius.tile}px`,
              background: c.surfaceHover,
              color: c.brandDark,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { background: '#e7f4e7', borderColor: c.borderHover },
            }}
          >
            <Icon name="arrow_back" size={24} />
          </Box>
          <Box>
            <Box component="div" sx={{ fontSize: 25, fontWeight: 800, color: c.textDarkest }}>
              Campanhas ativas
            </Box>
            <Box component="div" sx={{ fontSize: 15, color: c.textSecondary, mt: '2px' }}>
              Toque em uma campanha para ver os prêmios e participar
            </Box>
          </Box>
        </Box>

        {error && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: c.errorBg,
              border: `2px solid ${c.errorBorder}`,
              borderRadius: `${c.radius.input}px`,
              padding: '13px 15px',
            }}
          >
            <Icon name="error" size={22} color={c.error} />
            <Box component="span" sx={{ fontSize: 15, fontWeight: 700, color: c.errorTextDark }}>
              {error}
            </Box>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: c.brandMid }} size={48} />
          </Box>
        ) : campaigns.length === 0 ? (
          <Box
            sx={{
              background: c.surface,
              borderRadius: `${c.radius.card}px`,
              padding: '40px 24px',
              textAlign: 'center',
              boxShadow: c.shadows.card,
            }}
          >
            <Icon name="campaign" size={52} color={c.iconMuted} />
            <Box component="div" sx={{ fontSize: 19, fontWeight: 800, color: c.textSecondary, mt: '12px' }}>
              Nenhuma campanha ativa
            </Box>
            <Box component="div" sx={{ fontSize: 15, color: c.textTertiary, mt: '4px' }}>
              Não há campanhas ativas no momento. Volte em breve!
            </Box>
          </Box>
        ) : (
          campaigns.map((campaign, index) => (
            <Box
              key={campaign.id}
              sx={{
                background: c.surface,
                borderRadius: `${c.radius.cardLarge}px`,
                boxShadow: c.shadows.card,
                overflow: 'hidden',
                animation: `participantFadeUp ${0.35 + index * 0.05}s ease both`,
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <CampaignImage imageUrl={campaign.imageUrl} alt={campaign.name} width="100%" height={210} borderRadius={0} objectFit="cover" />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 14,
                    left: 14,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    background: c.brandMid,
                    color: c.onDarkPrimary,
                    borderRadius: `${c.radius.pill}px`,
                    padding: '7px 14px',
                    fontSize: 13.5,
                    fontWeight: 800,
                  }}
                >
                  <Icon name="radio_button_checked" size={16} />
                  EM ANDAMENTO
                </Box>
              </Box>

              <Box sx={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <Box>
                  <Box component="div" sx={{ fontSize: 22, fontWeight: 800, color: c.textDarkest }}>
                    {campaign.name}
                  </Box>
                  {campaign.description && (
                    <Box component="div" sx={{ fontSize: 15.5, color: c.textSecondary, mt: '4px' }}>
                      {campaign.description.length > 120 ? `${campaign.description.substring(0, 120)}...` : campaign.description}
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: c.surfaceTint,
                      color: c.successText,
                      borderRadius: `${c.radius.pill}px`,
                      padding: '6px 13px',
                      fontSize: 13.5,
                      fontWeight: 700,
                    }}
                  >
                    <Icon name="storefront" size={16} />
                    {campaign.stores?.length || 0} lojas
                  </Box>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: c.amberBg,
                      color: c.amberTextDark,
                      borderRadius: `${c.radius.pill}px`,
                      padding: '6px 13px',
                      fontSize: 13.5,
                      fontWeight: 700,
                    }}
                  >
                    <Icon name="calendar_month" size={16} />
                    Até {formatDate(campaign.endDate)}
                  </Box>
                </Box>

                <Box
                  component="button"
                  onClick={() => navigate(`/user/campaigns/${campaign.id}`)}
                  sx={{
                    width: '100%',
                    minHeight: 58,
                    border: 'none',
                    borderRadius: `${c.radius.button}px`,
                    background: c.gradients.primaryButton,
                    color: c.onDarkPrimary,
                    fontFamily: c.fontFamily,
                    fontSize: 17,
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: '0 6px 20px rgba(0,100,20,0.25)',
                    transition: 'transform 0.15s',
                    '&:hover': { transform: 'translateY(-2px)' },
                  }}
                >
                  <Icon name="visibility" size={22} />
                  Ver detalhes e participar
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default UserCampaignList;
